const MOTION_STORAGE_KEY = "galileo.einstein.motion";
const MOTION_ATTRIBUTE_VALUE = "reduced";

/**
 * App-level motion preference.
 *
 * `system` leaves the decision to the OS (`prefers-reduced-motion`).
 * `reduced` sets `html[data-motion="reduced"]`, which every stylesheet and
 * `useReducedMotionPreference()` treat exactly like the OS preference. There
 * is no option to force full motion over an OS reduce request.
 */
export type MotionPreference = "system" | "reduced";

export interface MotionPreferenceController {
    readonly getSnapshot: () => MotionPreference;
    readonly setPreference: (preference: MotionPreference) => void;
    readonly subscribe: (notify: () => void) => () => void;
    readonly dispose: () => void;
}

let activeController: MotionPreferenceController | undefined;

export function isMotionPreference(value: string): value is MotionPreference {
    return value === "system" || value === "reduced";
}

export function readStoredMotionPreference(storage: Storage | undefined): MotionPreference {
    if (storage === undefined) {
        return "system";
    }

    const stored = storage.getItem(MOTION_STORAGE_KEY);
    return stored !== null && isMotionPreference(stored) ? stored : "system";
}

export function applyMotionPreference(target: HTMLElement, preference: MotionPreference): void {
    if (preference === "reduced") {
        target.dataset.motion = MOTION_ATTRIBUTE_VALUE;
    } else {
        delete target.dataset.motion;
    }
}

export function initializeMotionPreference(
    target: HTMLElement = document.documentElement,
): MotionPreferenceController {
    activeController?.dispose();

    let preference = readStoredMotionPreference(getStorage());
    const listeners = new Set<() => void>();

    applyMotionPreference(target, preference);

    activeController = {
        getSnapshot: () => preference,
        setPreference(nextPreference) {
            if (nextPreference === preference) {
                return;
            }
            preference = nextPreference;
            const storage = getStorage();
            if (storage !== undefined) {
                storage.setItem(MOTION_STORAGE_KEY, nextPreference);
            }
            applyMotionPreference(target, preference);
            for (const listener of listeners) {
                listener();
            }
        },
        subscribe(notifyListener) {
            listeners.add(notifyListener);
            return () => {
                listeners.delete(notifyListener);
            };
        },
        dispose() {
            listeners.clear();
            if (activeController === this) {
                activeController = undefined;
            }
        },
    };

    return activeController;
}

export function getMotionPreferenceController(
    target: HTMLElement = document.documentElement,
): MotionPreferenceController {
    return activeController ?? initializeMotionPreference(target);
}

function getStorage(): Storage | undefined {
    if (typeof window === "undefined") {
        return undefined;
    }

    try {
        return window.localStorage;
    } catch {
        return undefined;
    }
}
